import { zodResolver } from '@hookform/resolvers/zod';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { downloadFile } from '@repo/ui/lib/download-file';
import { PinInput, PinInputGroup, PinInputSlot } from '@repo/ui/pin-input';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { renderSVG } from 'uqr';
import { z } from 'zod';
import { authClient, useSession } from '@/providers/session';
import { RecoveryCodeList } from './recovery-code-list';

export const ZEnable2FAForm = z.object({
  token: z.string(),
});

export type TEnable2FAForm = z.infer<typeof ZEnable2FAForm>;

export type EnableAuthenticatorAppDialogProps = {
  onSuccess?: () => void;
};

export const EnableAuthenticatorAppDialog = ({ onSuccess }: EnableAuthenticatorAppDialogProps) => {
  useLocale();

  const { refreshSession } = useSession();

  const [isOpen, setIsOpen] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [isSettingUp2FA, setIsSettingUp2FA] = useState(false);
  const [setup2FAData, setSetup2FAData] = useState<{ uri: string; secret: string } | null>(null);

  const enable2FAForm = useForm<TEnable2FAForm>({
    defaultValues: {
      token: '',
    },
    resolver: zodResolver(ZEnable2FAForm, { error: validationError }),
  });

  const { isSubmitting: isEnabling2FA } = enable2FAForm.formState;

  const setup2FA = async () => {
    if (isSettingUp2FA) {
      return;
    }

    setIsSettingUp2FA(true);
    setSetup2FAData(null);

    try {
      const data = await authClient.twoFactor.setup();
      await refreshSession();

      setSetup2FAData(data);
    } catch (err) {
      toast.error(t('auth.unableToSetupTwoFactorAuthentication'), {
        get description() {
          return t('auth.weWereUnableToSetupTwoFactorAuthenticationFor');
        },
      });
    }

    setIsSettingUp2FA(false);
  };

  const onEnable2FAFormSubmit = async ({ token }: TEnable2FAForm) => {
    try {
      const data = await authClient.twoFactor.enable({ code: token });
      await refreshSession();

      setRecoveryCodes(data.recoveryCodes);
      onSuccess?.();

      toast.success(t('auth.twoFactorAuthenticationEnabled'), {
        get description() {
          return t('auth.youWillNowBeRequiredToEnterACode');
        },
      });
    } catch (_err) {
      toast.error(t('auth.unableToSetupTwoFactorAuthentication'), {
        get description() {
          return t('auth.weWereUnableToSetupTwoFactorAuthenticationFor');
        },
      });
    }
  };

  const downloadRecoveryCodes = () => {
    if (recoveryCodes) {
      const blob = new Blob([recoveryCodes.join('\n')], {
        type: 'text/plain',
      });

      downloadFile({
        filename: 'raven-2FA-recovery-codes.txt',
        data: blob,
      });
    }
  };

  const handleEnable2FA = async () => {
    if (!setup2FAData) {
      await setup2FA();
    }

    setIsOpen(true);
  };

  useEffect(() => {
    enable2FAForm.reset();

    if (!isOpen && recoveryCodes && recoveryCodes.length > 0) {
      setRecoveryCodes(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger asChild={true}>
        <Button
          className="shrink-0"
          loading={isSettingUp2FA}
          onClick={(e) => {
            e.preventDefault();
            void handleEnable2FA();
          }}
        >
          {t('auth.enable2fa')}{' '}
        </Button>
      </DialogTrigger>
      <DialogContent>
        {setup2FAData &&
          (recoveryCodes ? (
            <div>
              <DialogHeader>
                <DialogTitle>{t('auth.backupCodes')}</DialogTitle>
                <DialogDescription>
                  {t('auth.yourRecoveryCodesAreListedBelowPleaseStoreThem')}{' '}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4">
                <RecoveryCodeList recoveryCodes={recoveryCodes} />
              </div>

              <DialogFooter className="mt-4">
                <DialogClose asChild>
                  <Button variant="secondary">{t('auth.close')}</Button>
                </DialogClose>

                <Button onClick={downloadRecoveryCodes}>{t('auth.download')}</Button>
              </DialogFooter>
            </div>
          ) : (
            <Form {...enable2FAForm}>
              <form onSubmit={enable2FAForm.handleSubmit(onEnable2FAFormSubmit)}>
                <DialogHeader>
                  <DialogTitle>{t('auth.enableAuthenticatorApp')}</DialogTitle>
                  <DialogDescription>
                    {t('auth.toEnableTwoFactorAuthenticationScanTheFollowingQr')}{' '}
                  </DialogDescription>
                </DialogHeader>

                <fieldset className="mt-4 flex flex-col gap-y-4" disabled={isEnabling2FA}>
                  <div
                    className="flex h-36 justify-center"
                    dangerouslySetInnerHTML={{
                      __html: renderSVG(setup2FAData?.uri ?? ''),
                    }}
                  />

                  <p className="text-muted-foreground text-sm">
                    {t('auth.ifYourAuthenticatorAppDoesNotSupportQrCodes')}{' '}
                  </p>

                  <p className="rounded-lg bg-muted/60 p-2 text-center font-mono text-muted-foreground tracking-widest">
                    {setup2FAData?.secret}
                  </p>

                  <p className="text-muted-foreground text-sm">
                    {t('auth.onceYouHaveScannedTheQrCodeOrEntered')}{' '}
                  </p>

                  <FormField
                    control={enable2FAForm.control}
                    name="token"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-muted-foreground">{t('auth.token')}</FormLabel>
                        <FormControl>
                          <PinInput {...field} maxLength={6} value={field.value ?? ''}>
                            {Array(6)
                              .fill(null)
                              .map((_, i) => (
                                <PinInputGroup key={i}>
                                  <PinInputSlot index={i} />
                                </PinInputGroup>
                              ))}
                          </PinInput>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="secondary">{t('reports.cancel')}</Button>
                    </DialogClose>

                    <Button loading={isEnabling2FA} type="submit">
                      {t('auth.enable2fa')}{' '}
                    </Button>
                  </DialogFooter>
                </fieldset>
              </form>
            </Form>
          ))}
      </DialogContent>
    </Dialog>
  );
};
